/**
 * MaintenanceOverlay Component
 *
 * Full-screen overlay that blocks all user interaction when maintenance mode is active.
 * Displays a maintenance message and prevents clicking on anything beneath it.
 *
 * Features:
 * - Fixed position covering entire viewport
 * - High z-index to appear above everything
 * - Custom message support with default fallback
 * - Subtle animation on appearance
 *
 * Usage:
 *   <MaintenanceOverlay
 *     isActive={maintenanceMode}
 *     message={maintenanceMessage}
 *   />
 */

import React from 'react';

export interface MaintenanceOverlayProps {
  /** Whether maintenance mode is active */
  isActive: boolean;
  /** Custom message to display (optional) */
  message?: string;
}

const DEFAULT_MESSAGE = 'We are currently performing scheduled maintenance. Please check back shortly.';

export const MaintenanceOverlay: React.FC<MaintenanceOverlayProps> = ({
  isActive,
  message,
}) => {
  if (!isActive) {
    return null;
  }

  const displayMessage = message?.trim() || DEFAULT_MESSAGE;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-chrome-strong via-chrome-deep to-chrome-strong"
      style={{ backdropFilter: 'blur(4px)' }}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative max-w-lg mx-4 text-center animate-fade-in">
        {/* Icon */}
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-warning-mid/20 rounded-full">
            <i className="fas fa-tools text-warning-light text-4xl"></i>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-ink-chrome mb-4">
          Under Maintenance
        </h1>

        {/* Message */}
        <p className="text-lg text-ink-chrome-muted mb-8 leading-relaxed">
          {displayMessage}
        </p>

        {/* Animated loading indicator */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-warning-light rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-warning-light rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-warning-light rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>

        {/* Subtle footer */}
        <p className="mt-8 text-sm text-ink-muted">
          This page will automatically update when maintenance is complete.
        </p>
      </div>
    </div>
  );
};

export default MaintenanceOverlay;
